import * as echarts from "echarts";
import moment from "moment/moment";
import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {useParams} from "react-router-dom";
import firebase, {db} from "../../config/Firebase";

const DpHisPage = () => {
  const {
    register,
    handleSubmit,
  } = useForm();

  const {ticket} = useParams();
  const [date, setDate] = useState(null);

  const upColor = "rgb(38, 166, 154)";
  const downColor = "rgb(239, 83, 80)";
  let myChart;

  const genOptions = (data) => ({
    title: {
      text: data.ticker,
      left: 0,
    },
    animation: false,
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "cross",
      },
      borderWidth: 1,
      borderColor: "#cccccc",
      padding: 10,
      textStyle: {
        color: "#000000",
      },
    },
    axisPointer: {
      link: [
        {
          xAxisIndex: "all",
        },
      ],
      label: {
        backgroundColor: "#777777",
      },
    },
    toolbox: {
      feature: {
        dataZoom: {
          yAxisIndex: false,
        },
        brush: {
          type: ["lineX", "clear"],
        },
      },
    },
    brush: {
      xAxisIndex: "all",
      brushLink: "all",
      outOfBrush: {
        colorAlpha: 0.1,
      },
    },
    visualMap: {
      show: false,
      seriesIndex: 1,
      dimension: 2,
      pieces: [
        {
          value: 1,
          color: downColor,
        },
        {
          value: -1,
          color: upColor,
        },
      ],
    },
    grid: [
      {
        left: "8%",
        right: "8%",
        height: "50%",
      },
      {
        left: "8%",
        right: "8%",
        top: "70%",
        height: "16%",
      },
    ],
    xAxis: [
      {
        type: "category",
        data: data.categoryData,
        boundaryGap: false,
        axisLine: {onZero: false},
        splitLine: {show: false},
        min: "dataMin",
        max: "dataMax",
        axisPointer: {
          z: 100,
        },
      },
      {
        type: "category",
        gridIndex: 1,
        data: data.categoryData,
        boundaryGap: false,
        axisLine: {onZero: false},
        axisTick: {show: false},
        splitLine: {show: false},
        axisLabel: {show: false},
        min: "dataMin",
        max: "dataMax",
      },
    ],
    yAxis: [
      {
        scale: true,
        splitArea: {
          show: true,
        },
      },
      {
        scale: true,
        gridIndex: 1,
        splitNumber: 2,
        axisLabel: {show: false},
        axisLine: {show: false},
        axisTick: {show: false},
        splitLine: {show: false},
      },
    ],
    dataZoom: [
      {
        type: "inside",
        xAxisIndex: [0, 1],
        start: 0,
        end: 100,
      },
      {
        show: true,
        xAxisIndex: [0, 1],
        type: "slider",
        top: "90%",
        start: 0,
        end: 100,
      },
    ],
    series: [
      {
        name: "日K",
        type: "candlestick",
        data: data.values,
        itemStyle: {
          color: upColor,
          color0: downColor,
          borderColor: undefined,
          borderColor0: undefined,
        },
        tooltip: {
          formatter: function(param) {
            param = param[0];
            return [
              "Date: " + param.name + `<hr style="margin: 3px 0">`,
              "Open: " + param.data[0] + "<br/>",
              "Close: " + param.data[1] + "<br/>",
              "Lowest: " + param.data[2] + "<br/>",
              "Highest: " + param.data[3] + "<br/>",
            ].join("");
          },
        },
        markLine: {
          symbol: ["none", "none"],
          data: data.lines,
        },
      },
      {
        name: "Volume",
        type: "bar",
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: data.volumes,
      },
    ],
  });

  const genEmptyOptions = (data) => ({
    title: {
      text: `${data.ticker} (No Data)`,
      center: 0,
    },
  });

  const getDate = (count = 0, timestamp = null) => {
    let today = timestamp === null ? moment() : moment(timestamp);
    today = today.add(count, "days");
    today = moment.tz(today, "America/New_York");
    return today.format('YYYY-MM-DD');
  };

  const formatter = (values, line) => {
    return values?.map((v) => {
      const date = getDate(0, parseInt(v["t"]));
      const volume = parseInt(
          line[date] ? line[date].volume : 0);
      const priceLines = line[date]?.priceLines || [];
      return [date, v["o"], v["c"], v["h"], v["l"], volume, priceLines];
    });
  };

  const splitData = (rawData) => {
    let categoryData = [];
    let values = [];
    let volumes = [];
    let linesSet = new Set([]);
    let lines = [];
    for (let i = 0; i < rawData.length; i++) {
      categoryData.push(rawData[i].splice(0, 1)[0]);
      values.push(rawData[i]);
      volumes.push([i, rawData[i][4], rawData[i][0] > rawData[i][1] ? 1 : -1]);

      rawData[i][5].forEach((price) => {
        linesSet.add(parseInt(price));
      });
    }

    linesSet.forEach((i) => {
      lines.push({yAxis: i});
    });

    return {
      categoryData: categoryData,
      values: values,
      volumes: volumes,
      lines: lines,
    };
  };

  const getCloudData = (data, line) => {
    const formData = {
      ticker: data.ticker,
      date: data.date,
      past: data.past,
    };
    const getData = firebase.functions().httpsCallable("GetDataDphis");

    getData({ params: formData }).then((res) => {
      myChart = echarts.init(document.getElementById("chart"));

      if (!res.data.results && line.length === 0) {
        myChart.setOption(genEmptyOptions(formData));
      } else {
        const strokes = splitData(formatter(res.data.results || [], line));
        myChart.setOption(genOptions({...strokes, ...formData}));
      }
    });
  };

  const onSubmit = (data) => {
    echarts.dispose(document.getElementById("chart"));

    data = {
      ...data,
      past: getDate(-180, data.date),
    };

    db.collection("dphis").doc(data.ticker).get().then((querySnapshot) => {
      const res = querySnapshot.data();
      const line = res === undefined ? [] : res;

      // Get cloud function data
      getCloudData(data, line);
    });
  };

  useEffect(() => {
    const nowDate = getDate();
    setDate(nowDate);

    const data = {
      date: nowDate,
      ticker: ticket || "AAPL",
    };

    onSubmit(data); // Auto load
  }, []);

  return (
      <div className="bg-light-2 pt-5 mt-5" style={{fontSize: `0.8rem`}}>
        <section className="pt-5 pb-5">
          <div className="container mb-5">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div
                  className="d-flex flex-column flex-md-row justify-content-center">
                <div className="mr-md-auto">
                  <input
                      {...register("ticker", {required: true})}
                      className="form-control"
                      type="text"
                      defaultValue={ticket || "AAPL"}
                      placeholder="Ticker"
                  />
                </div>
                <div className="mr-md-4 my-3 my-md-0">
                  <input
                      className="form-control"
                      type="date"
                      name="date"
                      defaultValue={date}
                      {...register("date", {required: true})}
                  />
                </div>
                <div className="px-0 col-12 col-md-3 col-lg-2">
                  <button
                      type="submit"
                      className="btn btn-call btn-block rounded-sm"
                  >
                    Search
                  </button>
                </div>
              </div>
            </form>
            <div className="bg-white p-3 mt-4" style={{height: "75vh"}}
                 id="chart">
              <h3>loading</h3>
            </div>
          </div>
        </section>
      </div>
  );
};

export default DpHisPage;
